/** @jsx jsx */

import Dropzone from 'react-dropzone';
import { ComponentProps } from 'react';

import {
  Button,
  Colors,
  Text,
  Tooltip,
  TooltipFocusableTrigger,
} from '@zapier/design-system';
import { css, jsx } from '@emotion/core';

const Styles = {
  root: css`
    flex: 1 auto;
  `,
  dropzone: ({ isReadOnly }: { isReadOnly?: boolean }) => [
    css`
      align-items: center;
      border-radius: 3px;
      border: 1px dashed ${Colors.GrayWarm6};
      cursor: ${isReadOnly ? 'not-allowed' : 'pointer'};
      display: flex;
      height: 84px;
      justify-content: center;
      margin: 0px;
      padding-top: 26px;
      padding-bottom: 26px;
      text-align: center;
      &:hover {
        border: 1px dashed
          ${isReadOnly ? Colors.GrayWarm6 : Colors.UiPrimaryStrongest};
      }
    `,
  ],
  buttonWrapper: css`
    align-items: center;
    display: flex;
    flex-direction: row;
  `,
};

type Props = {
  isReadOnly?: boolean;
  onDrop: ComponentProps<typeof Dropzone>['onDrop'];
};

function ImageDropzone({ isReadOnly, onDrop }: Props) {
  return (
    <div css={Styles.root}>
      {isReadOnly ? (
        <Tooltip content="Disabled" placement="bottom">
          <TooltipFocusableTrigger>
            <Dropzone
              accept={{
                'image/jpeg': ['.jpeg', '.jpg'],
                'image/png': ['.png'],
                'image/gif': ['.gif'],
              }}
              disabled={isReadOnly}
              multiple={false}
              noClick={isReadOnly}
              noDrag={isReadOnly}
              onDrop={onDrop}
            >
              {({ getRootProps, getInputProps }) => (
                <div {...getRootProps()} css={Styles.dropzone({ isReadOnly })}>
                  <input {...getInputProps()} />
                  <div css={Styles.buttonWrapper}>
                    <Text
                      color={isReadOnly ? 'GrayWarm5' : undefined}
                      margin="0 10px 0 0"
                    >
                      Drop image here or
                    </Text>
                    <Button isDisabled={isReadOnly}>select image</Button>
                  </div>
                </div>
              )}
            </Dropzone>
          </TooltipFocusableTrigger>
        </Tooltip>
      ) : (
        <Dropzone
          accept={{
            'image/jpeg': ['.jpeg', '.jpg'],
            'image/png': ['.png'],
            'image/gif': ['.gif'],
          }}
          disabled={isReadOnly}
          multiple={false}
          noClick={isReadOnly}
          noDrag={isReadOnly}
          onDrop={onDrop}
        >
          {({ getRootProps, getInputProps, open }) => (
            <div {...getRootProps()} css={Styles.dropzone({ isReadOnly })}>
              <input {...getInputProps()} />
              <div css={Styles.buttonWrapper}>
                <Text margin="0 10px 0 0">Drop image here or</Text>
                <Button onPress={() => open()}>select image</Button>
              </div>
            </div>
          )}
        </Dropzone>
      )}
    </div>
  );
}

export default ImageDropzone;
